import { useEffect, useRef, useState } from 'react';
import { analysisCategories } from '@/mocks/forexAnalysisPage';

type NavItem = {
  href: string;
  label: string;
  no?: string;
};

const pageLinks: NavItem[] = [
  { href: '#learning-path', label: 'เส้นทางการเรียนรู้' },
];

const afterLinks: NavItem[] = [
  { href: '#chart-case', label: 'ตัวอย่างกราฟ' },
  { href: '#indicators', label: 'อินดิเคเตอร์' },
  { href: '#mistakes', label: 'ข้อผิดพลาด' },
];

const categoryLinks: NavItem[] = analysisCategories.map((category) => ({
  href: `#tutorial-${category.id}`,
  label: category.title,
  no: category.no,
}));

const navItems: NavItem[] = [...pageLinks, ...categoryLinks, ...afterLinks];

const ACTIVE_OFFSET = 150;

export default function SectionNav() {
  const [active, setActive] = useState<string>(navItems[0].href.slice(1));
  const scrollerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ids = navItems.map((item) => item.href.slice(1));
    let frame = 0;

    const update = () => {
      frame = 0;
      let current = ids[0];
      ids.forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= ACTIVE_OFFSET) {
          current = id;
        }
      });
      setActive(current);
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const chip = scroller.querySelector<HTMLElement>(`[data-target="${active}"]`);
    if (!chip) return;

    const chipRect = chip.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const isOutOfView = chipRect.left < scrollerRect.left + 8 || chipRect.right > scrollerRect.right - 8;

    if (isOutOfView) {
      scroller.scrollBy({
        left: chipRect.left - scrollerRect.left - 32,
        behavior: 'smooth',
      });
    }
  }, [active]);

  return (
    <nav
      aria-label="เมนูหมวดบทความในหน้านี้"
      className="sticky top-16 z-40 border-b border-background-200 bg-background-50/95 backdrop-blur md:top-[72px]"
    >
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6 lg:px-10">
        <div className="flex items-center gap-3">
          <span className="hidden shrink-0 items-center gap-1.5 py-3 text-[11px] font-semibold uppercase tracking-wide text-foreground-500 sm:flex">
            <i className="ri-list-check text-sm text-primary-600" aria-hidden="true" />
            ในหน้านี้
          </span>

          <div className="relative min-w-0 flex-1">
            <div
              ref={scrollerRef}
              className="no-scrollbar flex items-center gap-1.5 overflow-x-auto py-2.5 md:gap-2"
            >
              {pageLinks.map((item) => (
                <NavChip key={item.href} item={item} active={active === item.href.slice(1)} />
              ))}

              <span
                aria-hidden="true"
                className="mx-1 h-5 w-px shrink-0 bg-background-300"
              />

              {categoryLinks.map((item) => (
                <NavChip key={item.href} item={item} active={active === item.href.slice(1)} />
              ))}

              <span
                aria-hidden="true"
                className="mx-1 h-5 w-px shrink-0 bg-background-300"
              />

              {afterLinks.map((item) => (
                <NavChip key={item.href} item={item} active={active === item.href.slice(1)} />
              ))}
            </div>

            <span
              aria-hidden="true"
              className="pointer-events-none absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-background-50 to-transparent"
            />
          </div>
        </div>
      </div>
    </nav>
  );
}

function NavChip({ item, active }: { item: NavItem; active: boolean }) {
  const target = item.href.slice(1);

  return (
    <a
      href={item.href}
      data-target={target}
      aria-current={active ? 'true' : undefined}
      className={`inline-flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
        active
          ? 'border-primary-300 bg-primary-100 text-primary-800'
          : 'border-transparent bg-background-100 text-foreground-700 hover:border-background-300 hover:bg-background-50 hover:text-primary-700'
      }`}
    >
      {item.no && (
        <span
          className={`font-num text-[10px] font-semibold ${
            active ? 'text-primary-700' : 'text-foreground-400'
          }`}
          aria-hidden="true"
        >
          {item.no}
        </span>
      )}
      {item.label}
    </a>
  );
}